/**
 * Os feedbacks recebidos, para o suporte da Cliqfy (#561).
 *
 * Só a organização de suporte tem esta tela no menu, e a API recusa as outras.
 * Uma lista de cartões, do mais novo para o mais antigo (v3): quem escreveu e
 * de que organização, de que tela, quando, o texto e as imagens. Era uma
 * tabela de seis colunas, e um texto longo numa célula estourava a página.
 *
 * As imagens abrem numa aba nova, no tamanho original: um print só se lê
 * inteiro. O link é assinado e vale de quatro a cinco minutos; a lista se
 * renova a cada três (`useFeedbacks`), e com ela os links. As miniaturas só
 * carregam quando aparecem na tela.
 */

import { useState, type ReactNode } from 'react';
import type { FeedbackDoSuporte } from '@cliqcrm/contracts';
import { Avatar, CabecalhoDeTela, Chip } from '@/shared/ui';
import { useFeedbacks } from '@/shared/api/consultas';
import { Carregando, Erro, Vazio } from '@/shared/ui/estados/estados';
import './feedbacks.css';

const dataEHora = (iso: string): string =>
  new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

export function TelaDeFeedbacks(): ReactNode {
  const consulta = useFeedbacks();

  if (consulta.error && !consulta.data) {
    return <Erro erro={consulta.error} aoRepetir={() => void consulta.refetch()} />;
  }
  if (!consulta.data) return <Carregando forma="lista" linhas={6} />;

  const { itens, total } = consulta.data;

  return (
    <div className="feedbacks">
      <CabecalhoDeTela
        titulo="Feedbacks"
        contagem={itens.length < total ? `${itens.length} de ${total}` : String(total)}
        descricaoDaContagem="Os mais recentes primeiro"
        apoio="O que quem usa o sistema mandou pelo botão de feedback, de todas as organizações."
      />
      {/* O cabeçalho fica parado e quem rola é o corpo — o molde das outras
          telas (ver `cabecalho-tela.css`). Com o respiro lateral no corpo e
          no cabeçalho, título e cartões começam na mesma coluna. */}
      <div className="feedbacks__corpo">
        {itens.length === 0 ? (
          <Vazio
            titulo="Nenhum feedback ainda"
            porque="Quando alguém mandar um feedback pelo item Feedback do menu, ele aparece aqui."
          />
        ) : (
          <ul className="feedbacks__lista" aria-label="Feedbacks recebidos">
            {itens.map((feedback) => (
              <li key={feedback.id} className="feedbacks__item">
                <Cartao feedback={feedback} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * Um feedback: quem, de onde e quando em cima; o texto no meio, com um teto
 * de linhas que se abre; as imagens embaixo.
 *
 * O teto é um botão com `aria-expanded`, e não `<details>`. Com `<details>`
 * fechado o navegador não desenha NADA além do `summary` — o começo do texto,
 * que o corte de seis linhas deveria mostrar, sumia, e o cartão ficava só com
 * "Ver o texto inteiro". Só aparece quando o texto é longo o bastante para
 * valer — textos curtos saem inteiros.
 */
function Cartao({ feedback }: { feedback: FeedbackDoSuporte }): ReactNode {
  const longo = feedback.texto.length > 420 || feedback.texto.split('\n').length > 6;
  const [aberto, definirAberto] = useState(false);

  return (
    <article className="feedbacks__cartao">
      <header className="feedbacks__cabeca">
        <Avatar nome={feedback.autor.nome} tamanho={36} />
        <div className="feedbacks__quem">
          <span className="feedbacks__nome">{feedback.autor.nome}</span>
          {feedback.autor.email && <span className="feedbacks__email">{feedback.autor.email}</span>}
        </div>
        <div className="feedbacks__de">
          <Chip tom="etapa">{feedback.organizacao.nome}</Chip>
          <Chip>
            <span title={feedback.tela.caminho}>{feedback.tela.nome ?? feedback.tela.caminho}</span>
          </Chip>
        </div>
        <time className="feedbacks__quando" dateTime={feedback.criado_em}>
          {dataEHora(feedback.criado_em)}
        </time>
      </header>

      <div className="feedbacks__corpo-do-cartao">
        {/* O corte mora no `span`, e não no parágrafo: com o respiro e o fundo
            no mesmo elemento, o corte de seis linhas deixava aparecer metade
            da sétima dentro do respiro de baixo. */}
        <p className="feedbacks__texto">
          <span className={longo && !aberto ? 'feedbacks__corte' : undefined}>
            {feedback.texto}
          </span>
        </p>
        {longo && (
          <button
            type="button"
            className="feedbacks__ver"
            aria-expanded={aberto}
            onClick={() => definirAberto(!aberto)}
          >
            {aberto ? 'Mostrar menos' : 'Ver o texto inteiro'}
          </button>
        )}

        {feedback.imagens.length > 0 && (
          <ul className="feedbacks__imagens" aria-label="Imagens do feedback">
            {feedback.imagens.map((imagem, indice) => (
              <li key={imagem.url}>
                <a
                  href={imagem.url}
                  target="_blank"
                  rel="noreferrer"
                  title="Abrir a imagem inteira numa aba nova"
                >
                  <img
                    src={imagem.url}
                    alt={`Imagem ${indice + 1} do feedback`}
                    loading="lazy"
                    decoding="async"
                  />
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}
